/**
 * Start the logic for the `AudioWidget.vue` component.
 */
export function initAudioWidgets(): void {
  const widgets = document.querySelectorAll('.audio-widget')
  widgets.forEach((playerElement) => {
    new AudioWidgetHandler(playerElement)
  })
}

class AudioWidgetHandler {
  audio: HTMLAudioElement
  button: HTMLButtonElement
  seeker: HTMLInputElement
  timeText: HTMLElement
  durationText: HTMLElement

  constructor(container: Element) {
    this.audio = container.getElementsByTagName('audio')[0]
    this.button = container.querySelector('.audio-widget__btn')!
    this.seeker = container.querySelector('.audio-widget__seeker')!
    this.timeText = container.querySelector('.audio-widget__current-time')!
    this.durationText = container.querySelector('.audio-widget__duration')!

    // Init the handler when the metadata has finished loading.
    if (this.audio.readyState > 0) {
      this.init()
    } else {
      this.audio.addEventListener('loadedmetadata', () => this.init())
    }
  }

  /**
   * Start the handler.
   *
   * Set the max value of the seeker input and the duration text
   * to the audio's duration, and start the event listeners for
   * playing / pausing, and seeking the audio.
   */
  init(): void {
    this.seeker.max = Math.floor(this.audio.duration).toString()
    this.seeker.disabled = false
    this.button.disabled = false
    this.durationText.textContent = this.timeToString(this.audio.duration)

    // Handles clicking on the play / pause button.
    this.button.addEventListener('click', () => this.toggle())
    // Updates the progress bar and current time text.
    this.audio.addEventListener('timeupdate', () => this.updateCurrentTime())
    // Hanldes clicking / seeking on the progress bar.
    this.seeker.addEventListener('input', () => {
      this.audio.currentTime = this.seeker.valueAsNumber
    })
  }

  /**
   * Update the progress bar value and the current time text.
   */
  updateCurrentTime(): void {
    const currentTime = this.audio.currentTime
    this.timeText.textContent = this.timeToString(currentTime)
    this.seeker.value = currentTime.toString()
    this.updateSeekerProgressCSS()

    if (this.audio.ended) {
      // The audio is automatically paused when it ends.
      // We call `this.pause()` here just to update the button state.
      this.pause()
    }
  }

  /**
   * Convert the given `timeInSeconds` to the `mm:ss` format.
   */
  timeToString(timeInSeconds: number): string {
    const minutes = Math.floor(timeInSeconds / 60).toString()
    const seconds = Math.floor(timeInSeconds % 60)
      .toString()
      .padStart(2, '0')

    return `${minutes}:${seconds}`
  }

  /**
   * Update the value of the progress bar CSS variable.
   *
   * We use this variable in the CSS to fill the progressed
   * part of the progress bar.
   */
  updateSeekerProgressCSS(): void {
    const currentProgressPercent =
      (this.seeker.valueAsNumber / this.audio.duration) * 100

    this.seeker.style.setProperty(
      '--input-value-percent',
      currentProgressPercent + '%',
    )
  }

  /**
   * Play / Pause the audio.
   */
  toggle(): void {
    if (this.audio.paused) {
      this.play()
    } else {
      this.pause()
    }
  }

  /**
   * Playing the audio
   */
  async play(): Promise<void> {
    try {
      await this.audio.play()
      this.button.setAttribute('aria-label', 'pause')
    } catch (error) {
      // This could happen if the user denies permission to play audio.
      this.button.setAttribute('aria-label', 'play')
    }
  }

  /**
   * Pause the audio.
   */
  pause(): void {
    this.audio.pause()
    this.button.setAttribute('aria-label', 'play')
  }
}
